<template>
  <div
    class="billing-credit-card-field__wrapper"
    :class="{
      ['input-active']: inputActive,
      ['has-value']: !!currentValue,
      [type]: true
    }"
  >
    <div class="value">
      <div v-if="inputActive || value" class="label">
        {{ label }}
      </div>

      <div v-if="inputActive">
        <input
          :name="name"
          :value="currentValue"
          :class="{ ['has-error']: errorMessage }"
          ref="input"
          @blur="_onInputBlur"
          @input="_onInput"
          @keypress="_filterInput"
          @keydown="_handleBackspace"
          @keypress.enter="_onInputBlur"
        />
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
      </div>

      <div v-else>
        <div v-if="type === 'ccNumber'" :class="{ ['empty']: !value }">
          <span
            v-for="(cardPartNumber, idx) of cardPartNumbers"
            :key="idx"
            class="part-number"
            :class="{ ['empty']: !value }"
            @click="_toggleInput"
          >
            {{ cardPartNumber }}
          </span>
        </div>

        <span
          v-else
          class="part-number"
          :class="{ ['empty']: !value }"
          @click="_toggleInput"
        >
          {{ value || _defaultValue }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

import {
  removeWhiteSpaceFromString,
  removeSlashFromString,
  isNextCharIsDigit
} from "@/utils/util";

import { cardCvc, cardExpiry, cardNumber } from "@/utils/validations";

export default Vue.component("BillingCreditCardField", {
  props: {
    name: VueTypes.string,
    value: VueTypes.oneOfType([VueTypes.number, VueTypes.string]),
    type: VueTypes.oneOf(["ccNumber", "exp", "cvv"]).def("ccNumber"),
    label: VueTypes.string,
    currentActive: VueTypes.oneOf(["ccNumber", "exp", "cvv", null]).def(null),
    cardNumberWidthSplitCount: VueTypes.number.def(10)
  },

  data() {
    return {
      inputActive: false,
      currentValue: "",
      errorMessage: null,
      splitString: new Array(this.cardNumberWidthSplitCount).join(" ")
    };
  },

  methods: {
    _toggleInput() {
      this.inputActive = !this.inputActive;

      if (this.currentActive === this.name) {
        this.$emit("update:currentActive", null);
      } else {
        this.$emit("update:currentActive", this.name);
      }

      // put focus into input on activation
      this.$nextTick(() => {
        if (this.inputActive) {
          this.$refs.input.focus();
        }
      });
    },

    _cleanErrors() {
      if (this.errorMessage) {
        this.errorMessage = null;
      }
    },

    _onInputBlur() {
      if (this.inputActive) {
        const error = this._postValidation;

        if (error) {
          this.errorMessage = error;
          this.$emit("change", this.name, "");
          return;
        }

        this._toggleInput();
        this._cleanErrors();
        this.$emit("change", this.name, this._cleanedValue);
      }
    },

    _checkExpField(val) {
      const valWOdash = removeSlashFromString(val);

      if (valWOdash.length > 4) {
        return false;
      }

      if (valWOdash) {
        const { length } = valWOdash;

        if (length === 1) {
          // month must start only with 0 or 1
          if (valWOdash > 1) {
            return false;
          }
        } else if (length === 2) {
          const [firstValue, secondValue] = valWOdash;
          const firstValueNum = +firstValue;
          const secondValueNum = +secondValue;

          // month can't be 00
          if (firstValueNum === 0 && secondValueNum === 0) {
            return false;
          }

          // tens must be 11 or 12
          if (
            firstValueNum === 1 &&
            (secondValueNum !== 1 && secondValueNum !== 2)
          ) {
            return false;
          }
        }
      }

      return true;
    },

    _isValid(val) {
      // allows only 4 symbols
      if (this.type === "exp") {
        return this._checkExpField(val);
      } else if (this.type === "ccNumber") {
        return removeWhiteSpaceFromString(val).length <= 16;
      } else {
        // CVV must be up to 4 symbols
        return val.length <= 4;
      }
    },

    _handleBackspace(e) {
      if (e.keyCode === 8) {
        this._cleanErrors();
        const lastChar = this.currentValue[this.currentValue.length - 1];
        const prevLastChar = this.currentValue[this.currentValue.length - 2];

        if (this.type === "exp") {
          if (lastChar === "/") {
            this.currentValue = this.currentValue[0];

            e.stopPropagation();
            e.preventDefault();
          }
        } else if (this.type === "ccNumber") {
          if (lastChar === " " || prevLastChar === " ") {
            this.currentValue = this.currentValue.substring(
              0,
              this.currentValue.length - this.cardNumberWidthSplitCount
            );

            e.stopPropagation();
            e.preventDefault();
          }
        }
      }
    },

    _filterInput(e) {
      const {
        target: { value },
        key: enteredNumber
      } = e;

      const passingVal = value + enteredNumber;

      // allow only digits
      if (
        !isNextCharIsDigit(e) ||
        !this._isValid(this._splitValue(passingVal))
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    },

    /**
     * Add spaces for every 4 characters
     * @returns {*}
     * @private
     */
    _numberSplitter(val) {
      const pureNumbers = removeWhiteSpaceFromString(val);

      if (
        pureNumbers !== "" &&
        pureNumbers.length % 4 === 0 &&
        pureNumbers.length < 16
      ) {
        return val + this.splitString;
      }

      return val;
    },

    /**
     * Add split sign for day/month displaying
     * @returns {*}
     * @private
     */
    _dateMonthSplitter(val) {
      if (val.length === 2) {
        return val + "/";
      }

      return val;
    },

    _splitValue(value) {
      let splitted = value;

      if (this.type === "ccNumber") {
        splitted = this._numberSplitter(value);
      } else if (this.type === "exp") {
        splitted = this._dateMonthSplitter(value);
      }

      return splitted;
    },

    _onInput(e) {
      const { value } = e.target;
      this._cleanErrors();
      this.currentValue = this._splitValue(value);
    }
  },

  computed: {
    /**
     * Value without any extra symbols (spaces, slashes etc)
     * @private
     */
    _cleanedValue() {
      switch (this.type) {
        // case "exp":
        //   return removeSlashFromString(this.currentValue);

        case "ccNumber":
          return removeWhiteSpaceFromString(this.currentValue);

        default:
          return this.currentValue;
      }
    },

    _defaultValue() {
      if (this.type === "exp") {
        return "MM/YY";
      }

      return "CVV";
    },

    /**
     * Validate after blurring the field
     * @returns {*}
     * @private
     */
    _postValidation() {
      switch (this.type) {
        case "exp":
          return cardExpiry(this.currentValue);

        case "cvv":
          return cardCvc(this.currentValue);

        case "ccNumber":
          return cardNumber(removeWhiteSpaceFromString(this.currentValue));

        default:
          return null;
      }
    },

    cardPartNumbers() {
      const val = this.value || "0000000000000000";
      const numbers = val.toString().match(/.{1,4}/g);
      return numbers ? numbers : [];
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "billing-credit-card-field";

.#{$ns} {
  &__wrapper {
    position: absolute;
    padding: 0 34px;

    &.cvv,
    &.exp {
      top: 183px;
    }

    &.ccNumber {
      top: 76px;
      width: 100%;
    }

    &.cvv {
      left: 244px;
    }

    .value {
      cursor: pointer;

      .part-number {
        padding-right: 46px;
        font-family: "Lato-Regular", sans-serif;
        font-size: 24px;
        color: $primary-color;
        font-weight: bold;

        &:nth-of-type(1) {
          padding-left: 7px;
        }

        &:last-child {
          margin-right: 0;
        }

        &.empty {
          color: #c4c4c4;
        }
      }

      .label {
        margin-bottom: 19px;
        text-align: left;
        font-family: "Lato-Bold", sans-serif;
        font-weight: 900;
        font-size: 15px;
        color: $main-text-color;
        padding-left: 10px;
      }

      input {
        font-family: "Lato-Regular", sans-serif;
        font-size: 16px;
        width: 100%;
        height: 40px;
        border: 1px solid $primary-color;
        border-radius: 3px;
        outline: none;
        padding: 0 10px;
        color: $title-text-color;

        &.has-error {
          border-color: $input-error-color;
        }
      }

      .error-message {
        font-family: "Lato-Light", sans-serif;
        font-size: 14px;
        margin-top: 9px;
        color: $input-error-color;
      }
    }

    &.input-active,
    &.has-value {
      &.ccNumber {
        top: 50px;
      }

      &.cvv,
      &.exp {
        top: 155px;
      }
    }

    &.input-active {
      .label {
        margin-bottom: 14px;
      }
    }
  }
}
</style>
